import { ResponsiveTable } from '../common/ResponsiveTable';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, getAuthHeaders } from '../../config/api';
import { useTheme } from '../../theme/ThemeContext';
import type { Appointment, CalendarAppointmentDetail } from '../calendar/types';
import { formatDate } from '../../utils/dateFormat';
import { BolivianDateInput } from '../common/BolivianDateInput';

type AllocationMode = 'oldest_debts_first' | 'current_appointment_first' | 'manual' | 'patient_credit';
type AccountEntryType = 'manual_charge' | 'adjustment' | 'discount' | 'refund';

const money = (value?: number | null) => `Bs ${Number(value || 0).toFixed(2)}`;

export default function PatientFinancePanel({ token, patientId }: { token: string; patientId: number }) {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const tt = useCallback((key: string, fallback: string) => t(`calendar.operational.${key}`, fallback), [t]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [appointmentId, setAppointmentId] = useState('');
  const [detail, setDetail] = useState<CalendarAppointmentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [payment, setPayment] = useState({ amount: '', method: 'Cash', date: new Date().toISOString().slice(0, 10), receiptNumber: '', payerType: 'Patient', payerName: '', notes: '', allocationMode: 'oldest_debts_first' as AllocationMode, manual: {} as Record<string, string> });
  const [entry, setEntry] = useState({ type: 'manual_charge' as AccountEntryType, concept: '', amount: '', date: new Date().toISOString().slice(0, 10), notes: '' });
  const [chargeAmount, setChargeAmount] = useState('');
  const [reservationAmount, setReservationAmount] = useState('');
  const [reservation, setReservation] = useState({ mode: 'payment' as 'payment' | 'refund', amount: '', method: 'Cash', notes: '' });

  const loadAppointments = useCallback(async () => {
    const response = await fetch(getApiUrl(`/patients/${patientId}/appointments`), { headers: getAuthHeaders(token) });
    if (!response.ok) throw new Error(await response.text() || 'Failed to load patient appointments');
    const data: Appointment[] = await response.json();
    const sorted = [...data].sort((left, right) => new Date(right.startTime || 0).getTime() - new Date(left.startTime || 0).getTime());
    setAppointments(sorted);
    setAppointmentId(current => current || (sorted[0] ? String(sorted[0].id) : ''));
  }, [patientId, token]);

  const loadDetail = useCallback(async (selectedId: string) => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    const response = await fetch(getApiUrl(`/appointments/${selectedId}/calendar-detail`), { headers: getAuthHeaders(token) });
    if (!response.ok) throw new Error(await response.text() || 'Failed to load patient finance');
    const data: CalendarAppointmentDetail = await response.json();
    setDetail(data);
    const selectedAppointment = appointments.find(item => String(item.id) === selectedId);
    setChargeAmount(selectedAppointment?.chargeAmount == null ? '' : String(selectedAppointment.chargeAmount));
    setReservationAmount(data.reservationFee == null ? '' : String(data.reservationFee.amount));
  }, [appointments, token]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadAppointments()
      .catch(caught => { if (!cancelled) setError(caught instanceof Error ? caught.message : 'Failed to load finance'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadAppointments]);

  useEffect(() => {
    if (!appointmentId) return;
    void loadDetail(appointmentId).catch(caught => setError(caught instanceof Error ? caught.message : 'Failed to load finance'));
  }, [appointmentId, loadDetail]);

  const refresh = async () => {
    await loadAppointments();
    await loadDetail(appointmentId);
  };

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await refresh();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Finance action failed');
    } finally {
      setBusy(false);
    }
  };

  const submitPayment = (event: React.FormEvent) => {
    event.preventDefault();
    if (!appointmentId) return;
    void run(async () => {
      const response = await fetch(getApiUrl(`/appointments/${appointmentId}/payments/collect`), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          amount: Number(payment.amount),
          method: payment.method,
          date: new Date(`${payment.date}T12:00:00`).toISOString(),
          receiptNumber: payment.receiptNumber || null,
          payerType: payment.payerType || null,
          payerName: payment.payerName || null,
          notes: payment.notes || null,
          allocationMode: payment.allocationMode,
          manualAllocations: payment.allocationMode === 'manual'
            ? Object.entries(payment.manual).filter(([, amount]) => Number(amount) > 0).map(([chargeId, amount]) => ({ chargeId, amount: Number(amount) }))
            : null
        })
      });
      if (!response.ok) throw new Error(await response.text() || 'Failed to register payment');
      setPayment(current => ({ ...current, amount: '', receiptNumber: '', payerName: '', notes: '', manual: {} }));
    });
  };

  const submitEntry = (event: React.FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const response = await fetch(getApiUrl(`/patients/${patientId}/account-entries`), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({
          type: entry.type,
          concept: entry.concept,
          amount: Number(entry.amount),
          date: new Date(`${entry.date}T12:00:00`).toISOString(),
          notes: entry.notes || null,
          appointmentId: appointmentId ? Number(appointmentId) : null
        })
      });
      if (!response.ok) throw new Error(await response.text() || 'Failed to add account entry');
      setEntry(current => ({ ...current, concept: '', amount: '', notes: '' }));
    });
  };

  const saveCharge = (event: React.FormEvent) => {
    event.preventDefault();
    if (!appointmentId) return;
    void run(async () => {
      const response = await fetch(getApiUrl(`/appointments/${appointmentId}/finance`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ chargeAmount: chargeAmount.trim() === '' ? null : Number(chargeAmount) })
      });
      if (!response.ok) throw new Error(await response.text() || 'Failed to update charge');
    });
  };

  const saveReservationAmount = (event: React.FormEvent) => {
    event.preventDefault();
    if (!appointmentId) return;
    void run(async () => {
      const response = await fetch(getApiUrl(`/appointments/${appointmentId}/reservation-fee`), {
        method: 'PUT',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ amount: reservationAmount.trim() === '' ? null : Number(reservationAmount) })
      });
      if (!response.ok) throw new Error(await response.text() || 'Failed to update reservation fee');
    });
  };

  const submitReservation = (event: React.FormEvent) => {
    event.preventDefault();
    if (!appointmentId) return;
    void run(async () => {
      const suffix = reservation.mode === 'refund' ? 'reservation-fee-refunds' : 'reservation-fee-payments';
      const response = await fetch(getApiUrl(`/appointments/${appointmentId}/${suffix}`), {
        method: 'POST',
        headers: getAuthHeaders(token),
        body: JSON.stringify({ amount: Number(reservation.amount), method: reservation.method, date: new Date().toISOString(), notes: reservation.notes || null })
      });
      if (!response.ok) throw new Error(await response.text() || 'Failed to update reservation fee');
      setReservation(current => ({ ...current, amount: '', notes: '' }));
    });
  };

  const deletePayment = (paymentId: number) => {
    if (!window.confirm(tt('deletePaymentConfirm', 'Delete this payment?'))) return;
    void run(async () => {
      const response = await fetch(getApiUrl(`/payments/${paymentId}`), {
        method: 'DELETE',
        headers: getAuthHeaders(token)
      });
      if (!response.ok) throw new Error(await response.text() || tt('errors.deletePaymentFailed', 'Failed to delete payment'));
    });
  };

  const manualTotal = useMemo(() => Object.values(payment.manual).reduce((sum, amount) => sum + Number(amount || 0), 0), [payment.manual]);
  const input: React.CSSProperties = { padding: '9px 10px', border: `1px solid ${tokens.app.inputBorder}`, borderRadius: 6, background: tokens.app.input, color: tokens.app.text, colorScheme: tokens.isLight ? 'light' : 'dark', minWidth: 0 };
  const label: React.CSSProperties = { display: 'grid', gap: 5, color: tokens.app.muted, fontSize: 12, fontWeight: 700 };
  const button: React.CSSProperties = { padding: '9px 12px', border: 0, borderRadius: 6, background: tokens.app.primary, color: tokens.app.primaryText, cursor: 'pointer', fontWeight: 700 };
  const card: React.CSSProperties = { padding: 14, border: `1px solid ${tokens.app.border}`, borderRadius: 8, background: tokens.app.surfaceAlt, display: 'grid', gap: 10 };

  if (loading) return <div style={{ color: tokens.app.text }}>{tt('loadingFinance', 'Loading finance...')}</div>;
  if (appointments.length === 0) return <div style={{ ...card, color: tokens.app.muted }}>{tt('noAppointmentsFinance', 'Patient has no appointments. Create appointment before recording appointment-linked finance.')}</div>;

  return <div style={{ display: 'grid', gap: 14, color: tokens.app.text }}>
    {error && <div role="alert" style={{ padding: 10, borderRadius: 6, background: '#7f1d1d', color: '#fecaca' }}>{error}</div>}
    <section style={card}>
      <label style={label}>{tt('referenceAppointment', 'Reference appointment')}
        <select style={input} value={appointmentId} onChange={event => setAppointmentId(event.target.value)}>
          {appointments.map(item => <option key={item.id} value={item.id}>{formatDate(item.startTime, 'Undated')} · {item.customReason || item.treatmentTemplate?.name || item.treatments?.map(treatment => treatment.name).join(', ') || `#${item.id}`}</option>)}
        </select>
      </label>
      <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <div><small style={{ color: tokens.app.muted }}>{tt('totalDue', 'Total due')}</small><strong style={{ display: 'block', color: Number(detail?.financial.totalDue || 0) > 0.005 ? '#ef4444' : tokens.app.text }}>{money(detail?.financial.totalDue)}</strong></div>
        <div><small style={{ color: tokens.app.muted }}>{tt('oldDebt', 'Old debt')}</small><strong style={{ display: 'block' }}>{money(detail?.financial.previousDebt)}</strong></div>
        <div><small style={{ color: tokens.app.muted }}>{tt('selectedAppointment', 'Selected appointment')}</small><strong style={{ display: 'block', color: Number(detail?.financial.currentAppointment || 0) > 0.005 ? '#ef4444' : tokens.app.text }}>{money(detail?.financial.currentAppointment)}</strong></div>
        <button type="button" style={button} onClick={() => window.print()}>{tt('actions.printStatement', 'Print statement')}</button>
      </div>
    </section>

    <section style={card}>
      <h3 style={{ margin: 0 }}>{tt('actions.registerPayment', 'Register payment')}</h3>
      <form onSubmit={submitPayment} style={{ display: 'grid', gap: 10 }}>
        <div className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
          <label style={label}>{tt('amount', 'Amount')}<input style={input} type="number" min="0.01" step="0.01" value={payment.amount} onChange={event => setPayment({ ...payment, amount: event.target.value })} required /></label>
          <label style={label}>{tt('method', 'Method')}<select style={input} value={payment.method} onChange={event => setPayment({ ...payment, method: event.target.value })}>{['Cash', 'QR', 'Card', 'Transfer', 'Other'].map(method => <option key={method}>{method}</option>)}</select></label>
          <label style={label}>{tt('date', 'Date')}<BolivianDateInput style={input} value={payment.date} onChange={date => setPayment({ ...payment, date })} /></label>
          <label style={label}>{tt('receipt', 'Receipt')}<input style={input} value={payment.receiptNumber} onChange={event => setPayment({ ...payment, receiptNumber: event.target.value })} /></label>
          <label style={label}>{tt('payerType', 'Payer type')}<select style={input} value={payment.payerType} onChange={event => setPayment({ ...payment, payerType: event.target.value })}>{['Patient', 'parent', 'spouse', 'other'].map(type => <option key={type}>{type}</option>)}</select></label>
          <label style={label}>{tt('whoPaid', 'Who paid?')}<input style={input} value={payment.payerName} onChange={event => setPayment({ ...payment, payerName: event.target.value })} /></label>
        </div>
        <label style={label}>{tt('notes', 'Notes')}<textarea style={input} value={payment.notes} onChange={event => setPayment({ ...payment, notes: event.target.value })} /></label>
        <div className="responsive-row" style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>{[
          ['oldest_debts_first', tt('allocation.oldestDebtsFirst', 'Oldest debts first')], ['current_appointment_first', tt('allocation.currentAppointmentFirst', 'Selected appointment first')], ['manual', tt('allocation.manual', 'Manual allocation')], ['patient_credit', tt('allocation.patientCredit', 'Patient credit')]
        ].map(([value, text]) => <label className="responsive-row" key={value} style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="radio" checked={payment.allocationMode === value} onChange={() => setPayment({ ...payment, allocationMode: value as AllocationMode })} />{text}</label>)}</div>
        {payment.allocationMode === 'manual' && <div style={{ display: 'grid', gap: 6 }}>
          {(detail?.financial.charges || []).map(charge => <label className="responsive-grid" key={charge.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 1fr) 100px 120px', gap: 8, alignItems: 'center' }}><span>{charge.concept}</span><span>{money(charge.pending)}</span><input style={input} type="number" min="0" max={charge.pending} step="0.01" value={payment.manual[charge.id] || ''} onChange={event => setPayment({ ...payment, manual: { ...payment.manual, [charge.id]: event.target.value } })} /></label>)}
          <strong>Allocated: {money(manualTotal)}</strong>
        </div>}
        <button type="submit" style={{ ...button, justifySelf: 'end' }} disabled={busy}>{tt('actions.registerPayment', 'Register payment')}</button>
      </form>
    </section>

    <section className="responsive-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
      <form onSubmit={saveCharge} style={card}><h3 style={{ margin: 0 }}>Appointment charge</h3><label style={label}>Charge<input style={input} type="number" min="0" step="0.01" value={chargeAmount} onChange={event => setChargeAmount(event.target.value)} /></label><button type="submit" style={button} disabled={busy}>Save charge</button></form>
      <form onSubmit={submitEntry} style={card}><h3 style={{ margin: 0 }}>Account entry</h3><label style={label}>Type<select style={input} value={entry.type} onChange={event => setEntry({ ...entry, type: event.target.value as AccountEntryType })}>{['manual_charge', 'adjustment', 'discount', 'refund'].map(type => <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>)}</select></label><label style={label}>Concept<input style={input} value={entry.concept} onChange={event => setEntry({ ...entry, concept: event.target.value })} required /></label><label style={label}>Amount<input style={input} type="number" min="0.01" step="0.01" value={entry.amount} onChange={event => setEntry({ ...entry, amount: event.target.value })} required /></label><label style={label}>Date<BolivianDateInput style={input} value={entry.date} onChange={date => setEntry({ ...entry, date })} /></label><label style={label}>Notes<textarea style={input} value={entry.notes} onChange={event => setEntry({ ...entry, notes: event.target.value })} /></label><button type="submit" style={button} disabled={busy}>Add entry</button></form>
    </section>

    <section className="responsive-grid" style={{ ...card, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
      <form onSubmit={saveReservationAmount} style={{ display: 'grid', gap: 8 }}><h3 style={{ margin: 0 }}>Reservation fee</h3><label style={label}>Configured amount<input style={input} type="number" min="0" step="0.01" value={reservationAmount} onChange={event => setReservationAmount(event.target.value)} /></label><button type="submit" style={button} disabled={busy}>Configure / waive</button></form>
      <form onSubmit={submitReservation} style={{ display: 'grid', gap: 8 }}><label style={label}>Action<select style={input} value={reservation.mode} onChange={event => setReservation({ ...reservation, mode: event.target.value as 'payment' | 'refund' })}><option value="payment">Record payment</option><option value="refund">Refund unused credit</option></select></label><label style={label}>Amount<input style={input} type="number" min="0.01" step="0.01" value={reservation.amount} onChange={event => setReservation({ ...reservation, amount: event.target.value })} required /></label><label style={label}>Method<select style={input} value={reservation.method} onChange={event => setReservation({ ...reservation, method: event.target.value })}>{['Cash', 'QR', 'Card', 'Transfer', 'Other'].map(method => <option key={method}>{method}</option>)}</select></label><label style={label}>Notes<textarea style={input} value={reservation.notes} onChange={event => setReservation({ ...reservation, notes: event.target.value })} /></label><button type="submit" style={button} disabled={busy}>Save</button></form>
    </section>

    <section style={card}>
      <h3 style={{ margin: 0 }}>{tt('paymentsStatement', 'Payments and statement')}</h3>
      <div className="responsive-table-scroll" style={{ overflowX: 'auto' }}>
        <ResponsiveTable><table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th align="left">{tt('date', 'Date')}</th><th align="right">{tt('amount', 'Amount')}</th><th align="left">{tt('method', 'Method')}</th><th align="left">{tt('appliedTo', 'Applied to')}</th><th align="left">{tt('receipt', 'Receipt')}</th><th /></tr></thead>
          <tbody>{(detail?.payments || []).map(item => <tr key={item.id}><td>{formatDate(item.date)}</td><td align="right">{money(item.amount)}</td><td>{item.method}</td><td>{item.appliedTo}</td><td>{item.receiptNumber || '-'}</td><td><button type="button" style={{ ...button, background: '#b91c1c' }} disabled={busy} onClick={() => deletePayment(item.id)}>{tt('actions.remove', 'Delete')}</button></td></tr>)}</tbody>
        </table></ResponsiveTable>
      </div>
    </section>
  </div>;
}
